import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useDebounce } from "@shared/hooks";
import { useDispatch, useSelector } from "react-redux";
import { CheckPicker } from "rsuite";
import { Option } from "@shared/interfaces";
import { getUniqueElements } from "@shared/utils";

import { Label } from "../Label";

interface AsyncCheckPickerInputProps<T> {
  labelTitle?: string;
  labelClassName?: string;
  values: Option[];
  initOptions?: Option[];
  staticOptions?: Option[];
  name: string;
  placeholder: string;
  className: string;
  disabled?: boolean;

  onChange?: (values: Option[]) => void;
  getData: (search: string) => void;
  selectData: any;
  prepareOptionFunction: (item: T) => Option;
  placement?: "auto";
}

const AsyncCheckPickerInput = <T,>(props: AsyncCheckPickerInputProps<T>) => {
  const {
    labelTitle,
    labelClassName,
    getData,
    selectData,
    prepareOptionFunction,
    onChange,
    name,
    placeholder,
    className,
    initOptions,
    disabled,
    staticOptions,
    placement,
  } = props;

  const dispatch = useDispatch();

  const [inputValue, setInputValue] = useState<string>("");

  const debounceValue = useDebounce<string>(inputValue, 500);
  const items: T[] = useSelector(selectData());

  const options: Option[] = useMemo(() => {
    return getUniqueElements(
      [
        ...(staticOptions || []),
        ...(initOptions || []),
        ...props.values,
        ...items.map((i) => prepareOptionFunction(i)),
      ],
      "value",
    );
  }, [items, prepareOptionFunction, props.values, initOptions, staticOptions]);

  const preparedValues: string[] = useMemo(() => props.values.map((v) => v.value), [props.values]);

  useEffect(() => {
    if (!disabled) {
      dispatch(getData(debounceValue));
    }
  }, [dispatch, getData, debounceValue, disabled]);

  const handleChange = useCallback(
    (value: string[]) => {
      const selectedValues = options.filter((option) => !!value.find((v) => option.value === v));
      onChange && onChange(selectedValues);
    },
    [options, onChange],
  );

  const onCloseMenu = useCallback(() => {
    if (debounceValue) {
      setInputValue("");
    }
  }, [debounceValue]);

  return (
    <>
      {labelTitle ? <Label title={labelTitle} className={labelClassName} /> : null}
      <CheckPicker
        disabled={disabled}
        className={className}
        onSearch={setInputValue}
        data={options}
        value={preparedValues}
        onChange={handleChange}
        name={name}
        placeholder={placeholder}
        onClose={onCloseMenu}
        placement={placement}
      />
    </>
  );
};

export default AsyncCheckPickerInput;
